import type { ReactNode } from 'react'
import type { ControllerRenderProps, FieldValues } from 'react-hook-form'

import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import type { TemplateVariableValidatorType } from '@/components/organisms/template-variable-editor/template-variable-validator.type'
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface FieldRendererProps {
  field: ControllerRenderProps<FieldValues, string>
  variable: ParsedTemplateVariable
  isDisabled: boolean
}

const INPUT_TYPE_MAP: Record<string, string> = {
  text: 'text',
  url: 'url',
  email: 'email',
}

const InputFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  variable,
  isDisabled,
}): ReactNode => (
  <Input
    {...field}
    type={INPUT_TYPE_MAP[variable.type]}
    disabled={isDisabled}
    placeholder={variable.label ?? variable.name}
    className={`template-variable-field-${variable.name}`}
  />
)

const NumberFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  variable,
  isDisabled,
}): ReactNode => (
  <Input
    {...field}
    type="number"
    disabled={isDisabled}
    placeholder={variable.label ?? variable.name}
    className={`template-variable-field-${variable.name}`}
    onChange={(e) => field.onChange(e.target.valueAsNumber)}
  />
)

const EnumFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  variable,
  isDisabled,
}): ReactNode => (
  <Select onValueChange={field.onChange} value={String(field.value)} disabled={isDisabled}>
    <SelectTrigger className={`template-variable-field-${variable.name}`}>
      <SelectValue placeholder={`Select ${variable.label ?? variable.name}`} />
    </SelectTrigger>
    <SelectContent>
      {variable.enumOptionList.map((option) => (
        <SelectItem key={option} value={option}>
          {option}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
)

const FIELD_RENDERER_MAP: Record<TemplateVariableValidatorType, React.FC<FieldRendererProps>> = {
  text: InputFieldRenderer,
  number: NumberFieldRenderer,
  url: InputFieldRenderer,
  email: InputFieldRenderer,
  enum: EnumFieldRenderer,
}

const capitalizeFirstLetter = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

interface TemplateVariableFormFieldContentProps {
  field: ControllerRenderProps<FieldValues, string>
  variable: ParsedTemplateVariable
  isDisabled: boolean
}

/**
 * Renders a single form field based on the template variable type.
 * Uses object lookup to map variable types to the appropriate input component.
 */
export const TemplateVariableFormFieldContent: React.FC<TemplateVariableFormFieldContentProps> = ({
  field,
  variable,
  isDisabled,
}): ReactNode => {
  const Renderer = FIELD_RENDERER_MAP[variable.type]
  const displayLabel = variable.label ?? capitalizeFirstLetter(variable.name)

  return (
    <FormItem>
      <FormLabel>{displayLabel}</FormLabel>
      <FormControl>
        <Renderer field={field} variable={variable} isDisabled={isDisabled} />
      </FormControl>
      {variable.helper && <FormDescription>{variable.helper}</FormDescription>}
      <FormMessage />
    </FormItem>
  )
}

TemplateVariableFormFieldContent.displayName = 'TemplateVariableFormFieldContent'
